/**
 * CMS Helper Utility
 * Fetches CMS content from the database - the SINGLE SOURCE OF TRUTH
 * 
 * IMPORTANT: All CMS data is stored in MongoDB and managed via Admin Panel.
 * This utility ONLY fetches from the database - no hardcoded defaults.
 * Data persists across server restarts, rebuilds, and redeployments.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * Fetch CMS content for a specific section from database
 * @param {string} section - The CMS section (e.g., 'home', 'about', 'contact')
 * @returns {Promise<Object>} - CMS content as key-value object
 */
export async function getCMSContent(section) {
  try {
    const response = await fetch(`${API_URL}/api/cms/${section}`);
    if (!response.ok) {
      console.warn(`CMS content not found for section: ${section}`);
      return {};
    }
    
    const data = await response.json();
    
    // Convert array of CMS items to object
    const content = {};
    data.forEach(item => {
      // Parse JSON/array values
      if (item.data_type === 'array' || item.data_type === 'json') {
        try {
          content[item.key] = typeof item.value === 'string' 
            ? JSON.parse(item.value) 
            : item.value;
        } catch (e) {
          content[item.key] = item.value;
        }
      } else {
        content[item.key] = item.value;
      }
    });
    
    return content;
  } catch (error) {
    console.error(`Error fetching CMS content for ${section}:`, error);
    return {};
  }
}

/**
 * Get CMS content with UI placeholder fallbacks
 * NOTE: These fallbacks are ONLY for UI display when database has no value yet.
 * They do NOT overwrite database values - database is always the source of truth.
 * 
 * @param {string} section - The CMS section
 * @param {Object} uiPlaceholders - Placeholder values for empty fields (UI display only)
 * @returns {Promise<Object>} - CMS content with placeholders for empty fields
 */
export async function getCMSContentWithDefaults(section, uiPlaceholders = {}) {
  const cmsContent = await getCMSContent(section);
  
  // Merge: CMS values take precedence, placeholders only fill empty/missing fields
  // This ensures database is ALWAYS the source of truth
  const result = { ...uiPlaceholders };
  
  Object.keys(cmsContent).forEach(key => {
    // Only use CMS value if it exists and is not empty
    if (cmsContent[key] !== undefined && cmsContent[key] !== null && cmsContent[key] !== '') {
      result[key] = cmsContent[key];
    }
  });
  
  return result;
}

/**
 * Construct full image URL from CMS path
 * @param {string} path - Image path from CMS (e.g., '/uploads/images/hero.jpg' or full URL)
 * @returns {string} - Full image URL
 */
export function getCMSImageURL(path) {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${API_URL}${path}`;
}
